// Invitación a una cuenta de demostración (CC-CONSOLE #1318).
//
// La liga que recibe el prospecto es `/register?demo=<token>`. El token viaja
// al backend en /auth/register y en /login (como `demoToken`) para que su
// usuario quede dentro de la compañía de su demo. Se guarda en sessionStorage
// porque el flujo cruza pantallas (registro → «ya tengo cuenta» → login) y
// recargas; es de un solo uso, así que se borra en cuanto el backend confirma
// que la cuenta ya tiene compañía.
//
// Es una credencial: quien la tenga puede reclamar la demo. Por eso se quita de
// la URL en cuanto se guarda, y eso pasa en el ARRANQUE (utils/demoInviteBoot,
// primer import de main.tsx), antes de que Sentry o PostHog lean
// `window.location`: si no, viajaría en el primer evento de cada uno. Aquí
// nunca se escribe en consola.
//
// Sin dependencias a propósito (ni React, ni router, ni analítica): tiene que
// poder correr antes que todo lo demás.

const KEY = "demoInviteToken";

// Respaldo si sessionStorage no está disponible (modo privado estricto): vive
// lo que viva la pestaña, que alcanza para registro → login sin recargar.
let memory: string | null = null;

// Misma forma que valida el backend (demoAccountsView.isPlausibleToken): lo
// que no la cumple ni se guarda ni se manda.
const TOKEN_RE = /^[A-Za-z0-9_-]{20,200}$/;

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    // Modo privado o almacenamiento bloqueado: se sigue sólo con la URL.
    return null;
  }
};

/** Token de `?demo=` de la URL actual, o null. */
export const demoTokenFromUrl = (search: string = window.location.search): string | null => {
  const token = new URLSearchParams(search).get("demo");
  return token && TOKEN_RE.test(token) ? token : null;
};

/** Guarda el token de la URL (si lo hay) para el resto del flujo. */
export const captureDemoToken = (search?: string): string | null => {
  const token = demoTokenFromUrl(search);
  if (token) {
    memory = token;
    try {
      storage()?.setItem(KEY, token);
    } catch {
      /* sin almacenamiento: el token sigue en la URL */
    }
  }
  return token;
};

/**
 * Guarda el token de la URL actual y lo QUITA de la barra de direcciones (sin
 * recargar ni dejar entrada en el historial). El resto de la URL —ruta, otros
 * parámetros, hash— queda igual. Nunca lanza: corre antes de montar la app.
 */
export const captureAndStripDemoToken = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("demo")) return null;
    const token = captureDemoToken(window.location.search);
    // Se quita aunque no tenga forma de token: tampoco debe quedarse a la vista.
    params.delete("demo");
    const query = params.toString();
    const clean = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", clean);
    return token;
  } catch {
    return null;
  }
};

/** El token vigente: el de la URL manda sobre el guardado. */
export const getDemoToken = (search?: string): string | undefined => {
  const fromUrl = captureDemoToken(search);
  if (fromUrl) return fromUrl;
  try {
    const stored = storage()?.getItem(KEY);
    if (stored && TOKEN_RE.test(stored)) return stored;
  } catch {
    /* sin almacenamiento: queda el respaldo en memoria */
  }
  return memory ?? undefined;
};

/** Ya se usó (o ya no aplica): que no se reenvíe en el siguiente login. */
export const clearDemoToken = (): void => {
  memory = null;
  try {
    storage()?.removeItem(KEY);
  } catch {
    /* nada que limpiar */
  }
};
